import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Pencil, Trash2, Plus, Upload } from 'lucide-react';
import { toast } from 'sonner';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', price: '', original_price: '', category: '', image_url: '', images: [], stock: '', variants: [], bulk_tiers: [], batch_number: '', expiry_date: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchProducts(); fetchCategories(); }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
      setProducts(data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/categories`);
      setCategories(data);
    } catch {}
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const currentImages = formData.images || [];
    if (currentImages.length >= 5) { toast.error('Maximum 5 images allowed'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      const newImages = [...currentImages, url];
      setFormData(prev => ({ ...prev, images: newImages, image_url: prev.image_url || url }));
      toast.success(`Image ${newImages.length}/5 uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddImageUrl = () => {
    const currentImages = formData.images || [];
    if (currentImages.length >= 5) { toast.error('Maximum 5 images allowed'); return; }
    if (!formData.image_url) { toast.error('Enter a URL first'); return; }
    if (!/^https?:\/\/.+/i.test(formData.image_url)) { toast.error('Invalid URL. Must start with http:// or https://'); return; }
    if (!currentImages.includes(formData.image_url)) {
      setFormData(prev => ({ ...prev, images: [...currentImages, prev.image_url] }));
      toast.success(`Image ${currentImages.length + 1}/5 added`);
    }
  };

  const removeGalleryImage = (idx) => {
    const newImages = formData.images.filter((_, i) => i !== idx);
    setFormData(prev => ({ ...prev, images: newImages, image_url: newImages[0] || prev.image_url }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, price: parseFloat(formData.price), original_price: parseFloat(formData.original_price) || 0, stock: parseInt(formData.stock), images: formData.images || [], variants: formData.variants.map(v => ({ weight: v.weight, price: parseFloat(v.price) || 0 })), bulk_tiers: (formData.bulk_tiers || []).filter(t => t.min_qty && t.discount_percent).map(t => ({ min_qty: parseInt(t.min_qty), discount_percent: parseFloat(t.discount_percent) })), batch_number: (formData.batch_number || '').trim(), expiry_date: (formData.expiry_date || '').trim() };
      if (editingProduct) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/products/${editingProduct.product_id}`, payload, { withCredentials: true });
        toast.success('Product updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/products`, payload, { withCredentials: true });
        toast.success('Product created');
      }
      setIsDialogOpen(false);
      setEditingProduct(null);
      setFormData({ name: '', description: '', price: '', original_price: '', category: categories[0]?.name || '', image_url: '', images: [], stock: '', variants: [], bulk_tiers: [], batch_number: '', expiry_date: '' });
      fetchProducts();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({ name: product.name, description: product.description, price: product.price.toString(), original_price: (product.original_price || '').toString(), category: product.category, image_url: product.image_url, images: product.images || [], stock: product.stock.toString(), variants: (product.variants || []).map(v => ({ weight: v.weight || '', price: (v.price || '').toString() })), bulk_tiers: (product.bulk_tiers || []).map(t => ({ min_qty: t.min_qty?.toString() || '', discount_percent: t.discount_percent?.toString() || '' })), batch_number: product.batch_number || '', expiry_date: product.expiry_date || '' });
    setIsDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>);
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-black font-heading" data-testid="admin-products-heading">Manage Products</h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingProduct(null); setFormData({ name: '', description: '', price: '', category: categories[0]?.name || '', image_url: '', images: [], stock: '', variants: [], bulk_tiers: [], batch_number: '', expiry_date: '' }); }} data-testid="add-product-button">
                <Plus className="mr-2 h-4 w-4" /> Add Tea
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle data-testid="product-dialog-title">{editingProduct ? 'Edit Product' : 'Add New Tea'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="product-name-input" />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="product-description-input" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="price">Selling Price ({'\u20B9'})</Label>
                    <Input id="price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required data-testid="product-price-input" />
                  </div>
                  <div>
                    <Label htmlFor="original_price">MRP ({'\u20B9'})</Label>
                    <Input id="original_price" type="number" step="0.01" value={formData.original_price} onChange={(e) => setFormData({ ...formData, original_price: e.target.value })} placeholder="Optional" data-testid="product-original-price-input" />
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock</Label>
                    <Input id="stock" type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required data-testid="product-stock-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <select id="category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="product-category-input">
                    {categories.map(c => <option key={c.category_id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Product Images (up to 5)</Label>
                  <div className="flex gap-2 mb-2">
                    <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Paste URL and click Add, or Upload" data-testid="product-image-input" className="flex-1" />
                    <Button type="button" variant="outline" size="sm" className="flex-shrink-0" onClick={handleAddImageUrl} disabled={(formData.images || []).length >= 5} data-testid="product-image-add-url-button">Add</Button>
                    <Button type="button" variant="outline" size="sm" className="flex-shrink-0 relative" disabled={uploading || (formData.images || []).length >= 5} data-testid="product-image-upload-button">
                      <Upload className="h-4 w-4 mr-1" /> {uploading ? '...' : 'Upload'}
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="product-image-file-input" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-2">{(formData.images || []).length}/5 images added</p>
                  {(formData.images || []).length > 0 && (
                    <div className="flex gap-2 flex-wrap" data-testid="product-image-gallery">
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img} alt={`Image ${idx + 1}`} className="h-16 w-16 object-cover rounded-lg border border-[#E0E0E0]" />
                          <button type="button" onClick={() => removeGalleryImage(idx)} className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${idx}`}>&times;</button>
                          {idx === 0 && <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center bg-primary/80 text-white rounded-b-lg">Cover</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Variants (Weight / Price)</Label>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setFormData({ ...formData, variants: [...formData.variants, { weight: '', price: '' }] })} data-testid="add-variant-button">+ Add Variant</Button>
                  </div>
                  {formData.variants.length === 0 && <p className="text-xs text-muted-foreground">No variants. Click "Add Variant" to add weight/price options.</p>}
                  <div className="space-y-2">
                    {formData.variants.map((v, idx) => (
                      <div key={idx} className="flex gap-2 items-center" data-testid={`variant-row-${idx}`}>
                        <Input placeholder="e.g. 50g" value={v.weight} onChange={(e) => { const variants = [...formData.variants]; variants[idx] = { ...variants[idx], weight: e.target.value }; setFormData({ ...formData, variants }); }} data-testid={`variant-weight-${idx}`} />
                        <Input type="number" step="0.01" placeholder="Price ₹" value={v.price} onChange={(e) => { const variants = [...formData.variants]; variants[idx] = { ...variants[idx], price: e.target.value }; setFormData({ ...formData, variants }); }} data-testid={`variant-price-${idx}`} />
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => { const variants = formData.variants.filter((_, i) => i !== idx); setFormData({ ...formData, variants }); }} data-testid={`remove-variant-${idx}`}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 border-t border-[#E0E0E0] pt-4" data-testid="batch-expiry-section">
                  <Label>Batch &amp; Expiry</Label>
                  <p className="text-[11px] text-muted-foreground">Displayed together on the product page so shoppers see freshness info at a glance.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="batch_number" className="text-xs">Batch Number</Label>
                      <Input id="batch_number" placeholder="e.g. RJ-2026-A1" value={formData.batch_number} onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} data-testid="product-batch-number-input" />
                    </div>
                    <div>
                      <Label htmlFor="expiry_date" className="text-xs">Expiry Date</Label>
                      <Input id="expiry_date" type="date" value={formData.expiry_date} onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })} data-testid="product-expiry-date-input" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 border-t border-[#E0E0E0] pt-4" data-testid="bulk-tiers-section">
                  <div className="flex items-center justify-between">
                    <Label>Bulk Discount Tiers (qty &amp; % off)</Label>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setFormData({ ...formData, bulk_tiers: [...(formData.bulk_tiers || []), { min_qty: '', discount_percent: '' }] })} data-testid="add-tier-button">+ Add Tier</Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Customer pays full price for fewer items; once they buy <strong>min_qty</strong> or more, the entire line gets <strong>discount %</strong> off. Min qty must be ≥ 2.</p>
                  {(formData.bulk_tiers || []).length === 0 && <p className="text-xs text-muted-foreground">No tiers configured. e.g. 5+ → 5%, 10+ → 10%.</p>}
                  <div className="space-y-2">
                    {(formData.bulk_tiers || []).map((t, idx) => (
                      <div key={idx} className="flex gap-2 items-center" data-testid={`tier-row-${idx}`}>
                        <Input type="number" min={2} placeholder="Min qty (e.g. 5)" value={t.min_qty} onChange={(e) => { const tiers = [...formData.bulk_tiers]; tiers[idx] = { ...tiers[idx], min_qty: e.target.value }; setFormData({ ...formData, bulk_tiers: tiers }); }} data-testid={`tier-minqty-${idx}`} />
                        <Input type="number" min={0} max={90} step="0.5" placeholder="Discount %" value={t.discount_percent} onChange={(e) => { const tiers = [...formData.bulk_tiers]; tiers[idx] = { ...tiers[idx], discount_percent: e.target.value }; setFormData({ ...formData, bulk_tiers: tiers }); }} data-testid={`tier-percent-${idx}`} />
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => { const tiers = formData.bulk_tiers.filter((_, i) => i !== idx); setFormData({ ...formData, bulk_tiers: tiers }); }} data-testid={`remove-tier-${idx}`}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="product-submit-button">{editingProduct ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {products.map((product) => (
            <div key={product.product_id} className="flex gap-4 p-4 rounded-xl bg-white border border-[#E0E0E0]" data-testid={`admin-product-${product.product_id}`}>
              <img src={product.image_url} alt={product.name} className="w-16 h-16 object-cover rounded-lg" />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold font-heading text-sm">{product.name}</h3>
                <p className="text-xs text-muted-foreground">{product.category}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <div className="text-right">
                  <p className="font-bold text-primary text-sm">{'\u20B9'}{product.price}</p>
                  <p className="text-xs text-muted-foreground">Stock: {product.stock}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.product_id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleDelete(product.product_id)} data-testid={`delete-product-${product.product_id}`}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
